// src/api/corpus.js
//
// Thin client for the proxy's HAR-corpus + platform-trend-monitor REST
// endpoints (docs/har-corpus-spec.md — WP5 §8 "Frontend"). Mirrors the
// request() helper pattern in api/launches.js / api/checks.js: same base-URL
// resolution (REACT_APP_PROXY_API_URL in prod, falls back to a bare "/proxy"
// path relative to the page origin locally).
//
//   GET  /proxy/corpus/scorecard?app=<id>
//     -> { cohorts: { [platform]: { metrics, series (last 14 sessions),
//                                    alerts, advisories, ... } } }
//        (exact per-cohort shape is intentionally NOT locked down here —
//        callers defensive-parse; see corpus/metrics.js emptyMetrics(),
//        corpus/trendDetectors.js alert shape, corpus/advisories.js
//        advisory shape on the proxy side for the field names in play)
//
//   POST /proxy/corpus/compare  { app, platform, rows }
//     -> { metrics, alerts, advisories, baseline }
//        `rows` is the SLIM projection built by utils/corpusProjection.js —
//        never raw dashboard rows.
//
// These routes are being added concurrently by another agent
// (proxy-server/apiServer.js) — they may not exist yet, or may 404/500 while
// the corpus is empty/cold. Both functions swallow "route not there yet" /
// "nothing to show yet" outcomes to null so TrendsPage can render a quiet
// empty state instead of surfacing a scary error. Any other failure
// (network down, 5xx, malformed JSON) rejects so the caller can distinguish
// "quietly nothing" from "something's actually broken".

const PROXY_API_BASE = (process.env.REACT_APP_PROXY_API_URL || '/proxy').replace(/\/$/, '');

/**
 * Internal: GET/POST JSON against the proxy REST API. Always resolves the
 * parsed JSON body on 2xx. On a non-2xx response, rejects with an Error whose
 * `.status` and `.body` carry the server's status code and parsed error body
 * — mirrors api/launches.js's request() exactly.
 */
async function request(path, init = {}) {
  let res;
  try {
    res = await fetch(`${PROXY_API_BASE}/proxy${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...(init.headers || {}),
      },
    });
  } catch (e) {
    const err = new Error(`Could not reach the proxy server at ${PROXY_API_BASE}`);
    err.status = 0;
    err.body = null;
    err.cause = e;
    throw err;
  }

  let json = null;
  try { json = await res.json(); } catch { /* empty/non-JSON body */ }

  if (!res.ok) {
    const err = new Error((json && (json.detail || json.error)) || `${init.method || 'GET'} ${path} failed (${res.status})`);
    err.status = res.status;
    err.body = json;
    throw err;
  }
  return json;
}

/**
 * GET /proxy/corpus/scorecard?app=<id> -> the parsed scorecard object, or
 * null STRICTLY when the route isn't live yet (404/204) — i.e. "this proxy
 * doesn't have the corpus endpoints at all". A successful-but-empty
 * response (the app simply has no corpus history yet — cold start) is
 * returned as-is (normalized to `{cohorts: {}}` if the body was empty/odd)
 * so callers can tell "route missing" apart from "route works, nothing
 * captured yet" and render distinct empty-state copy for each. Any other
 * failure (network unreachable, 5xx) rejects — TrendsPage should surface
 * that distinctly from both quiet-empty cases.
 */
export async function getScorecard(app) {
  try {
    const qs = app ? `?app=${encodeURIComponent(app)}` : '';
    const data = await request(`/corpus/scorecard${qs}`, { method: 'GET' });
    if (!data || typeof data !== 'object') return { cohorts: {} };
    return data;
  } catch (e) {
    if (e && (e.status === 404 || e.status === 204)) return null;
    throw e;
  }
}

/**
 * POST /proxy/corpus/compare { app, platform, rows } -> { metrics, alerts,
 * advisories, baseline }, or null when the route isn't live yet (404) — same
 * "quiet empty state, not an error" contract as getScorecard(). `rows` should
 * already be the slim projection from utils/corpusProjection.js — this
 * function does not re-project.
 */
export async function compareSession({ app, platform, rows } = {}) {
  try {
    const data = await request('/corpus/compare', {
      method: 'POST',
      body: JSON.stringify({
        app,
        platform,
        rows: Array.isArray(rows) ? rows : [],
      }),
    });
    return data && typeof data === 'object' ? data : null;
  } catch (e) {
    if (e && e.status === 404) return null;
    throw e;
  }
}
